import {
  ActiveIcon,
  InActiveIcon,
  LeftArrowIcon,
  RightArrowIcon,
  SearchIcon,
  SortDownIcon,
  SuspendIcon,
  ViewEyeIcon,
} from "@/assets/icons/admin";
import CustomDropdown from "@/components/admin/customdropdown";
import {
  Button,
  getKeyValue,
  Input,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@nextui-org/react";
import { useEffect, useState } from "react";
import RequestDetailTab from "./requestDetail";
import { RequestDetails } from "@/types";
import { useGetAllRequests } from "@/services/request.service";

const RequestList = () => {
  const statuses = [
    { label: "All", value: "all" },
    { label: "Solved", value: "SOLVED" },
    { label: "Waiting for an answer", value: "WAITING" },
    { label: "Rejected", value: "REJECTED" },
  ];

  const columns = [
    {
      key: "id",
      label: "Req No",
    },
    {
      key: "createdAt",
      label: "Sent Date",
    },
    {
      key: "userName",
      label: "Name",
    },
    {
      key: "userEmail",
      label: "Email",
    },
    {
      key: "topic",
      label: "Topic",
    },
    {
      key: "title",
      label: "Title",
    },
    {
      key: "question",
      label: "Question",
    },
    {
      key: "status",
      label: "Status",
    },
    {
      key: "action",
      label: "Action",
    },
  ];

  const [tableRows, setTableRows] = useState<RequestDetails[]>([]);
  const { data: getRequests } = useGetAllRequests({ page: 1, pageSize: 10 });

  useEffect(() => {
    if (getRequests?.data?.data?.length) {
      setTableRows(getRequests.data.data);
    } else {
      setTableRows([]);
    }
    console.log(tableRows);
  }, [getRequests]);

  const [filters, setFilters] = useState({
    status: "all",
    search: "",
  });
  const [roleFilteredRows, setRoleFilteredRows] = useState<RequestDetails[]>(
    [],
  );

  const [selectedStatus, setSelectedStatus] = useState("all");
  const handleDropdownSelect = (selectedOption: any) => {
    setFilters({ ...filters, status: selectedOption.value });
    setSelectedStatus(selectedOption.value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ ...filters, search: e.target.value });
  };

  useEffect(() => {
    let filteredRows = tableRows;

    // Filter by role
    if (filters.status !== "all") {
      filteredRows = tableRows.filter((row) => row.status === filters.status);
    }

    // Filter by search
    if (filters.search !== "") {
      const keyword = filters.search.toLowerCase();
      filteredRows = filteredRows.filter((row) =>
        Object.values(row).some(
          (value) => value && String(value).toLowerCase().includes(keyword),
        ),
      );
    }
    setRoleFilteredRows(filteredRows);
    setCurrentPage(1);
  }, [tableRows, filters]);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPageOptions = ["All", "3", "5", "10"];
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(e.target.value);
    setCurrentPage(1);
  };

  const handleClickNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handleClickPrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState("");

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const formatDateTime = (isoString: string | number | Date) => {
    const date = new Date(isoString);

    const padZero = (num: number) => (num < 10 ? "0" : "") + num;

    const day = padZero(date.getDate());
    const month = padZero(date.getMonth() + 1); // getMonth() returns month from 0 to 11
    const year = date.getFullYear(); // Get full year
    const hours = padZero(date.getHours());
    const minutes = padZero(date.getMinutes());
    const seconds = padZero(date.getSeconds()); // Add this line to get seconds

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; // Include seconds in the returned string
  };

  const sortRows = (rows: RequestDetails[], key: string) => {
    return rows.sort((a, b) => {
      let aValue, bValue;
      aValue = getKeyValue(a, key);
      bValue = getKeyValue(b, key);
      if (key === "no") {
        aValue = tableRows.findIndex((row) => row.id === a.id) + 1;
        bValue = tableRows.findIndex((row) => row.id === b.id) + 1;
      }

      if (sortOrder === "asc") {
        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return aValue - bValue;
        } else {
          return String(aValue).localeCompare(String(bValue));
        }
      } else {
        if (typeof aValue === "string" && typeof bValue === "string") {
          return bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return bValue - aValue;
        } else {
          return String(bValue).localeCompare(String(aValue));
        }
      }
    });
  };

  let filteredRows = roleFilteredRows;
  // Apply sorting
  if (sortBy) {
    filteredRows = sortRows(filteredRows, sortBy);
  }
  if (rowsPerPage !== "All") {
    filteredRows = roleFilteredRows.slice(
      (currentPage - 1) * parseInt(rowsPerPage),
      currentPage * parseInt(rowsPerPage),
    );
  }

  const totalRows = tableRows.length;
  const startRow = (currentPage - 1) * filteredRows.length + 1;
  const endRow = Math.min(currentPage * filteredRows.length, totalRows);

  const rowsRange = `${startRow}-${endRow} out of ${totalRows}`;

  const [isViewingRequest, setIsViewingRequest] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RequestDetails | null>(null);
  const handleViewRequestClick = (item: RequestDetails) => {
    setSelectedItem(item);
    setIsViewingRequest(true);
  };

  const handleViewRequestCancel = () => {
    setIsViewingRequest(false);
  };

  const handleSortColumn = (columnKey: string) => {
    if (sortBy === columnKey) {
      toggleSortOrder(); // Toggle the sorting order if the same column is clicked
    } else {
      setSortBy(columnKey); // Set a new column to sort by
      setSortOrder("asc"); // Default to ascending order when a new column is selected
    }
  };

  return (
    <div>
      {isViewingRequest ? (
        <RequestDetailTab
          onCancel={handleViewRequestCancel}
          item={selectedItem}
        />
      ) : (
        <div>
          <div>
            <div className="flex w-full flex-col gap-4">
              <div className="mb-6 mt-2 flex w-auto flex-wrap items-start gap-3 sm:gap-4 md:mb-0 md:flex-nowrap">
                <div className="w-[48%] sm:w-2/4">
                  <CustomDropdown
                    options={statuses}
                    onSelect={handleDropdownSelect}
                    placeholder="All"
                    value={selectedStatus}
                  />
                </div>
                <Input
                  className="input-bg w-[48%] sm:w-2/4"
                  isClearable
                  classNames={{
                    input: [
                      "mr-0",
                      "text-white/90 dark:text-white/90",
                      "placeholder:text-white/60 ml-4",
                    ],
                    inputWrapper: [
                      "shadow-xl",
                      "!cursor-text",
                      "bg-white-5",
                      "text-[13px]",
                    ],
                  }}
                  placeholder="Search Keyword"
                  startContent={<SearchIcon />}
                  onChange={handleSearchChange}
                ></Input>
                <Button className="admin-common-button-gradient font-ubuntu w-full rounded-md text-base font-bold sm:w-[20%]">
                  Apply
                </Button>
                <div className="hidden w-[40%] sm:flex">
                  {/* Hidden in smaller screens */}
                </div>
              </div>
            </div>
            <div className="mt-5 w-full">
              <div className="w-full overflow-x-auto">
                <Table
                  removeWrapper
                  className="table-striped table-striped-even table"
                >
                  <TableHeader className="rounded-none" columns={columns}>
                    {(column) => (
                      <TableColumn key={column.key}>
                        <div className="white-op flex">
                          {column.label}
                          <Button
                            isIconOnly
                            onClick={() => handleSortColumn(column.key)}
                            className="transparent-bg h-auto w-auto"
                          >
                            <SortDownIcon />
                          </Button>
                        </div>
                      </TableColumn>
                    )}
                  </TableHeader>
                  <TableBody
                    items={filteredRows}
                    emptyContent={"No rows to display."}
                  >
                    {(item) => (
                      <TableRow key={item.id}>
                        {(columnKey) => (
                          <TableCell>
                            {columnKey === "id" ? (
                              <div className="font-bold">
                                {"#" + getKeyValue(item, columnKey)}
                              </div>
                            ) : columnKey === "title" ? (
                              <div className="font-bold">
                                {getKeyValue(item, columnKey)}
                              </div>
                            ) : columnKey === "status" ? (
                              <div className="flex items-center">
                                {item.status === "SOLVED" ? (
                                  <span>
                                    <ActiveIcon />
                                  </span>
                                ) : item.status === "REJECTED" ? (
                                  <span>
                                    <InActiveIcon />
                                  </span>
                                ) : (
                                  <span>
                                    <SuspendIcon />
                                  </span>
                                )}
                              </div>
                            ) : columnKey === "action" ? (
                              <div>
                                <Button
                                  onClick={() => handleViewRequestClick(item)}
                                  isIconOnly
                                  className="transparent-bg h-auto w-auto"
                                >
                                  <ViewEyeIcon />
                                </Button>
                              </div>
                            ) : columnKey === "createdAt" ? (
                              <div className="w-max">
                                <div>{formatDateTime(item?.createdAt)}</div>
                              </div>
                            ) : (
                              <div className="font-normal">
                                {getKeyValue(item, columnKey)}
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="table-striped-even mt-1	flex items-center text-sm sm:justify-end sm:text-base">
                <div className="ml-[15px] justify-start sm:ml-0 sm:ml-4 sm:mr-8">
                  <label
                    htmlFor="rowsPerPage"
                    className="font-sans opacity-30 sm:mr-2"
                  >
                    Rows per page:
                  </label>
                  <select
                    id="rowsPerPage"
                    className="select-menu font-open-sans rounded border-none bg-transparent px-2 py-1 font-normal text-gray-500"
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                  >
                    {rowsPerPageOptions.map((option) => (
                      <option
                        key={option}
                        value={option}
                        className="font-open-sans text-gray-600 sm:font-normal"
                      >
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex">
                  <div className="ml-[8px] mt-2 opacity-30 sm:ml-[45px] sm:mr-8">
                    <p>{rowsRange}</p>
                  </div>
                  <button
                    onClick={handleClickPrevPage}
                    className="ml-[18px] mr-2 text-xs sm:ml-0 sm:mr-3"
                  >
                    <LeftArrowIcon />
                  </button>
                  <button onClick={handleClickNextPage} className="text-xs">
                    <RightArrowIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestList;
